import { EContractStatus, EWorksheetStatus } from '@if/core/types'
import { Action } from '@if/mui'
import { useCreditApplicationById } from '@src/data/api/credit-api/credit-api'
import { useDownloadTemplateContractPdf } from '@src/data/api/credit-api/files-api'
import { useSendSignatureLink } from '@src/data/api/worksheet-api/worksheet-api'
import { cvtAwaitingApproval } from '@src/data/creditapp-selectors'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Download, FileSignature, Send } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// Contract-signature actions (edit / sign digitally / sign manually) with their async + polling state.
export const useContractAwaitingSignature = (creditApp: CreditApplication, isExpired: boolean): Action[] => {
  const { t } = useTranslation()

  // Sign digitally: send the signature link, then poll while the contract is being sent.
  const [shouldPoll, setShouldPoll] = useState(creditApp.contract.status === EContractStatus.Sending)
  const [sendSignatureLink, isSendingSignatureLink] = useSendSignatureLink(creditApp.financingProgramId, creditApp.id)
  const [fullCreditApplication, isFetching] = useCreditApplicationById(
    { creditApplicationId: creditApp.id, financingProgramId: creditApp.financingProgramId },
    shouldPoll,
  )
  const polledStatus = fullCreditApplication?.contract?.status
  useEffect(() => {
    setShouldPoll(polledStatus === EContractStatus.Sending)
  }, [polledStatus])

  // Sign manually: download the contract template PDF.
  const [downloadPdf, isDownloading] = useDownloadTemplateContractPdf(creditApp.financingProgramId, creditApp.id)

  const isDraft = creditApp.worksheet?.status === EWorksheetStatus.Draft
  const isSendingSignature = isSendingSignatureLink || shouldPoll

  const editContract: Action = {
    icon: FileSignature,
    label: isExpired
      ? t('worksheet.updateContract')
      : creditApp.worksheet != null && !isDraft
        ? t('worksheet.editWorksheet')
        : t('worksheet.createContract'),
    href: `/credits/${creditApp.financingProgramId}/${creditApp.id}/worksheet`,
  }
  const signDigitally: Action = {
    icon: Send,
    label: isSendingSignature
      ? t('common.sending')
      : creditApp.hasOngoingElectronicSignature
        ? t('worksheet.signDigitallyLinkSent')
        : t('worksheet.signDigitally'),
    disabled: isFetching || isSendingSignature,
    onClick: () => void sendSignatureLink(creditApp.id),
  }
  const signManually: Action = {
    icon: Download,
    label: t('worksheet.signManually'),
    disabled: isDownloading,
    onClick: () => void downloadPdf(creditApp.contract.cvtNumber),
  }

  return isExpired
    ? [editContract]
    : [...(cvtAwaitingApproval(creditApp.requiredDocuments) ? [] : [editContract]), signDigitally, signManually]
}
